/**
 * Authenticated fetch wrapper.
 *
 * Authentication travels via the `ori_session` HttpOnly cookie that
 * the server sets at /r/<token> redirect, onboarding submit, and
 * plan-signup submit. Fetch sends same-origin cookies by default,
 * so we don't have to do anything special — except be explicit with
 * `credentials: 'same-origin'` to defend against future global
 * defaults changing.
 *
 * On 401 the cached UI hint in localStorage is cleared so the next
 * read returns null and the SPA can route through onboarding.
 */

import { clearCachedSession } from './session-storage';
import { getTraceId } from '../telemetry/trace-context';
import { noteLiveVersion } from '../../../../shared-web/stale-bundle-reload';

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * When true, sends no credentials (no cookie). Useful for read-only
   * endpoints that should always be treated as anonymous regardless of
   * session. Do NOT use on requests whose response sets the session
   * cookie — `credentials: 'omit'` makes the browser drop `Set-Cookie`.
   */
  anonymous?: boolean;
  /**
   * Sets the `Idempotency-Key` header. Mint one per user action
   * (crypto.randomUUID()) and reuse it when retrying the same action so
   * the server replays the first result instead of re-executing.
   */
  idempotencyKey?: string;
}

/**
 * Oriscen closed this account (2026-10-21 Riley): any request answered
 * 403 { error: 'account_closed' } — a write refused, or the 401 of the session
 * the close revoked (edge auth/middleware.js) — says so on window, and
 * SessionProvider switches the app to the closed screen at once.
 */
export const ACCOUNT_CLOSED_EVENT = 'ember:account-closed';

function noteAccountClosed(status: number, body: unknown): void {
  if (status !== 403 || !body || typeof body !== 'object') return;
  if ((body as { error?: unknown }).error !== 'account_closed') return;
  try { window.dispatchEvent(new Event(ACCOUNT_CLOSED_EVENT)); } catch { /* no window (SSR) */ }
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * A body that could not be read at all: a response cut off mid-flight, a
 * connection dropped between headers and body, JSON that stops mid-object.
 * Kept distinct from a body that legitimately parsed to `null`, which is a
 * value some caller may one day want to see.
 */
const UNREADABLE = Symbol('unreadable-body');

async function readBody(res: Response): Promise<unknown> {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { return await res.json(); } catch { return UNREADABLE; }
  }
  try { return await res.text(); } catch { return UNREADABLE; }
}

export async function apiFetch<T = unknown>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers ?? {});
  // Attach the per-link trace id so the server resumes the same
  // correlation context that started in EffectWorker. Empty when the
  // SPA wasn't reached via /r/, in which case the server mints an
  // orphan trace at the request boundary.
  const traceId = getTraceId();
  if (traceId && !headers.has('X-Ori-Trace-Id')) {
    headers.set('X-Ori-Trace-Id', traceId);
  }
  if (opts.idempotencyKey && !headers.has('Idempotency-Key')) {
    headers.set('Idempotency-Key', opts.idempotencyKey);
  }
  let body: BodyInit | undefined;
  if (opts.body !== undefined && opts.body !== null) {
    if (
      typeof opts.body === 'string'
      || opts.body instanceof FormData
      || opts.body instanceof Blob
      || opts.body instanceof URLSearchParams
    ) {
      body = opts.body;
    } else {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(opts.body);
    }
  }
  const init: RequestInit = {
    ...opts,
    headers,
    body,
    credentials: opts.anonymous ? 'omit' : 'same-origin',
  };
  // A request that died before any response — the browser put it on a
  // connection the other side had already closed and, unlike a GET, does not
  // repeat a POST on its own (prod 2026-09-16: one iOS Safari, 27 sends over
  // an evening, each after 25–54s of typing, each refused on the spot and each
  // landing when the person tapped again). One that carries an idempotency
  // key is sent once more: the edge replays a request it already served, so
  // the retry cannot double the action. An abort is the caller's own call and
  // is not retried.
  const res = await fetch(path, init).catch((err: unknown) => {
    if (!opts.idempotencyKey || opts.signal?.aborted) throw err;
    return fetch(path, init);
  });
  // The deploy that answered: a newer one than this bundle reloads the page
  // (stale-bundle-reload) unless a send is on its way.
  noteLiveVersion(res.headers.get('X-App-Version'));
  if (res.status === 401) {
    // Server says our session is bad; drop the local UI cache so the
    // next protected call sees an anonymous state.
    clearCachedSession();
  }
  const parsed = await readBody(res);
  if (!res.ok) {
    noteAccountClosed(res.status, parsed);
    throw new ApiError(res.status, parsed === UNREADABLE ? null : parsed);
  }
  // A 2xx whose body never arrived is a failed read, not a successful call
  // that returned nothing. Handing back `null` under a type that promises a
  // response object only moved the crash somewhere far away: prod 2026-09-10
  // 23:16, a truncated poll response became `null`, and Talk's setMessages
  // updater read `.messages` off it from inside React's render, past every
  // try/catch on the call site, taking the whole page down for someone
  // forty-three minutes into a telling. Thrown here it lands in the caller's
  // own catch, where the retry and the failure event already live.
  if (parsed === UNREADABLE) {
    throw new ApiError(res.status, null, `HTTP ${res.status} with an unreadable body`);
  }
  return parsed as T;
}

/**
 * The same request, answered as the words come (2026-09-21): the call asks
 * for `text/event-stream`; the edge streams `data:` frames and closes with a
 * terminal frame `{done: true, ...result}`, which resolves the call (minus
 * `done`). Every earlier frame goes to `onFrame`. An edge that answered whole
 * instead — JSON, because nothing streamed or the turn was refused before any
 * word — resolves or throws exactly as apiFetch would, so the caller sees one
 * contract either way. A frame `{error}` after the words began is the call
 * failing (ApiError with that body); a stream cut off before its terminal
 * frame is a failed read, like a truncated body.
 */
export async function apiFetchStream<T = unknown>(
  path: string,
  opts: ApiFetchOptions & { onFrame: (frame: Record<string, unknown>) => void },
): Promise<T> {
  const headers = new Headers(opts.headers ?? {});
  headers.set('Accept', 'text/event-stream');
  const traceId = getTraceId();
  if (traceId && !headers.has('X-Ori-Trace-Id')) headers.set('X-Ori-Trace-Id', traceId);
  if (opts.idempotencyKey && !headers.has('Idempotency-Key')) headers.set('Idempotency-Key', opts.idempotencyKey);
  headers.set('Content-Type', 'application/json');
  const { onFrame, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    headers,
    body: JSON.stringify(opts.body ?? {}),
    credentials: opts.anonymous ? 'omit' : 'same-origin',
  };
  // One retry on a request that died before any response, as apiFetch does
  // for a keyed action (the edge replays a request it already served).
  const res = await fetch(path, init).catch((err: unknown) => {
    if (!opts.idempotencyKey || opts.signal?.aborted) throw err;
    return fetch(path, init);
  });
  if (res.status === 401) clearCachedSession();
  noteLiveVersion(res.headers.get('X-App-Version'));
  if (!String(res.headers.get('content-type') || '').includes('text/event-stream')) {
    const parsed = await readBody(res);
    if (!res.ok) {
      noteAccountClosed(res.status, parsed);
      throw new ApiError(res.status, parsed === UNREADABLE ? null : parsed);
    }
    if (parsed === UNREADABLE) throw new ApiError(res.status, null, `HTTP ${res.status} with an unreadable body`);
    return parsed as T;
  }
  if (!res.body) throw new ApiError(res.status, null, `HTTP ${res.status} with no stream`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (err) {
      throw new ApiError(res.status, null, err instanceof Error ? err.message : 'stream read failed');
    }
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });
    let sep = buffer.indexOf('\n\n');
    while (sep !== -1) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      sep = buffer.indexOf('\n\n');
      const line = raw.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      let frame: Record<string, unknown>;
      try { frame = JSON.parse(line.slice(5).trim()) as Record<string, unknown>; } catch { continue; }
      if (frame.error !== undefined) throw new ApiError(res.status, frame, `stream error: ${String(frame.error)}`);
      if (frame.done === true) {
        const { done: _done, ...result } = frame;
        return result as T;
      }
      onFrame(frame);
    }
  }
  throw new ApiError(res.status, null, 'stream ended before its terminal frame');
}
